import { useState, useEffect } from "react";
import {
  FaUsers,
  FaArrowTrendUp,
  FaArrowTrendDown,
  FaMoneyBillWave,
  FaUserCheck,
  FaCircleUser,
} from "react-icons/fa6";
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  BarElement,
  ArcElement,
  Title,
  Tooltip,
  Legend,
  Filler,
} from "chart.js";
import { Line, Bar } from "react-chartjs-2";
import { Header } from "../../../components/Header";
import { DashboardLayout } from "../../../components/layouts/DashboardLayout";
import { Helmet } from "react-helmet";
import { useNavigate } from "react-router-dom";
import { BaseUrl } from "../../../utilities/BaseUrl";
import { useHookDashboardSummery } from "../../../hooks/useHookDashboardSummery";

// Register Chart.js components
ChartJS.register(
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  BarElement,
  ArcElement,
  Title,
  Tooltip,
  Legend,
  Filler
);

/* ============================================
   Dark Theme Chart.js Defaults
   ============================================ */
ChartJS.defaults.color = "#64748B";
ChartJS.defaults.borderColor = "rgba(51, 65, 85, 0.2)";

const TOOLTIP_STYLE = {
  backgroundColor: "rgba(11, 20, 38, 0.95)",
  titleColor: "#F0F4F8",
  bodyColor: "#94A3B8",
  borderColor: "rgba(0, 180, 216, 0.2)",
  borderWidth: 1,
  padding: 14,
  cornerRadius: 10,
  displayColors: true,
  boxPadding: 6,
  titleFont: { size: 13, weight: "600" },
  bodyFont: { size: 12 },
  usePointStyle: true,
};

const GRID_STYLE = {
  color: "rgba(51, 65, 85, 0.2)",
  drawBorder: false,
};

const TICK_STYLE = {
  color: "#64748B",
  font: { size: 11, weight: "500" },
};

/* ============================================
   Skeleton Pulse
   ============================================ */
const SkeletonPulse = ({ className }) => (
  <div className={`animate-pulse rounded-lg bg-slate-700/40 ${className}`} />
);

/* ============================================
   KOMPONEN: StatCard
   ============================================ */
function StatCard({ title, value, icon: Icon, gradient, iconBg, trend, isLoading }) {
  const isCurrency =
    title.includes("Pemasukan") ||
    title.includes("Pengeluaran") ||
    title.includes("Saldo");
  const isPositive = trend > 0;

  return (
    <article
      className="rounded-2xl p-5 transition-all duration-300 hover:-translate-y-1 group"
      style={{
        background: "rgba(15, 23, 42, 0.5)",
        border: "1px solid rgba(51, 65, 85, 0.3)",
        backdropFilter: "blur(12px)",
      }}
      onMouseEnter={(e) => {
        e.currentTarget.style.borderColor = "rgba(0, 180, 216, 0.15)";
        e.currentTarget.style.boxShadow = "0 8px 30px rgba(0, 0, 0, 0.2)";
      }}
      onMouseLeave={(e) => {
        e.currentTarget.style.borderColor = "rgba(51, 65, 85, 0.3)";
        e.currentTarget.style.boxShadow = "none";
      }}
    >
      <div className="flex items-start justify-between mb-4">
        <div
          className="w-11 h-11 rounded-xl flex items-center justify-center transition-transform duration-300 group-hover:scale-110"
          style={{
            background: iconBg,
            border: `1px solid ${gradient}`,
            boxShadow: `0 4px 12px ${gradient}20`,
          }}
        >
          <Icon className="text-lg" style={{ color: gradient }} />
        </div>

        {trend !== undefined && (
          <div
            className={`flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-semibold ${
              isPositive
                ? "bg-emerald-500/10 text-emerald-400 border border-emerald-500/20"
                : "bg-red-500/10 text-red-400 border border-red-500/20"
            }`}
          >
            {isPositive ? (
              <FaArrowTrendUp className="text-[10px]" />
            ) : (
              <FaArrowTrendDown className="text-[10px]" />
            )}
            {Math.abs(trend)}%
          </div>
        )}
      </div>

      <p className="text-xs font-medium text-slate-500 mb-1.5 uppercase tracking-wider">
        {title}
      </p>

      {isLoading ? (
        <SkeletonPulse className="h-7 w-3/4" />
      ) : (
        <p className="text-2xl font-display font-bold text-gray-100 tracking-tight">
          {isCurrency
            ? new Intl.NumberFormat("id-ID", {
                style: "currency",
                currency: "IDR",
                minimumFractionDigits: 0,
                maximumFractionDigits: 0,
              }).format(value || 0)
            : (value || 0).toLocaleString("id-ID")}
        </p>
      )}
    </article>
  );
}

/* ============================================
   KOMPONEN: ChartCard
   ============================================ */
function ChartCard({ title, children }) {
  return (
    <article
      className="rounded-2xl p-5 sm:p-6"
      style={{
        background: "rgba(15, 23, 42, 0.5)",
        border: "1px solid rgba(51, 65, 85, 0.3)",
        backdropFilter: "blur(12px)",
      }}
    >
      <h3 className="font-display text-base sm:text-lg font-bold text-gray-100 mb-5">
        {title}
      </h3>
      {children}
    </article>
  );
}

/* ============================================
   KOMPONEN UTAMA: DashboardMenu
   ============================================ */
export const DashboardMenu = () => {
  const navigate = useNavigate();
  const [showSidebar, setShowSidebar] = useState(true);
  const [userData, setUserData] = useState(null);

  // Ambil data dari Hook API
  const { data, loading } = useHookDashboardSummery();

  console.log(data);

  useEffect(() => {
    const u = localStorage.getItem("user_data-radiustrend");
    if (!u) {
      navigate("/login");
    }
    setUserData(JSON.parse(u));
  }, []);

  const formatCurrency = (value) =>
    new Intl.NumberFormat("id-ID", {
      style: "currency",
      currency: "IDR",
      minimumFractionDigits: 0,
      maximumFractionDigits: 0,
    }).format(value);

  /* ---------- Mapping Stat Cards dari API ---------- */
  const statCards = [
    {
      title: "Total User PPoE",
      value: data?.pppoe,
      icon: FaUsers,
      gradient: "#48CAE4",
      iconBg: "rgba(72, 202, 228, 0.12)",
      trend: 12, // Trend statis, bisa diubah ke dinamis jika API menyediakan
    },
    {
      title: "Total Pemasukan",
      value: data?.income,
      icon: FaArrowTrendUp,
      gradient: "#34d399",
      iconBg: "rgba(52, 211, 153, 0.12)",
      trend: 8,
    },
    {
      title: "Total Pengeluaran",
      value: data?.outcome,
      icon: FaArrowTrendDown,
      gradient: "#f87171",
      iconBg: "rgba(248, 113, 113, 0.12)",
      trend: -5,
    },
    {
      title: "Total Saldo",
      value: data?.saldo,
      icon: FaMoneyBillWave,
      gradient: "#a78bfa",
      iconBg: "rgba(167, 139, 250, 0.12)",
      trend: 15,
    },
  ];

  /* ---------- Chart Data (Sementara Dummy, Nanti bisa diganti API) ---------- */
  const chartLabels = ["Jan", "Feb", "Mar", "Apr", "Mei", "Jun"];

  const pemasukanPengeluaranData = {
    labels: chartLabels,
    datasets: [
      {
        label: "Pemasukan",
        data: [4000, 3000, 2000, 2780, 1890, 2390],
        borderColor: "#34d399",
        backgroundColor: "rgba(52, 211, 153, 0.08)",
        fill: true,
        tension: 0.4,
        pointRadius: 4,
        pointHoverRadius: 7,
        pointBackgroundColor: "#34d399",
        pointBorderColor: "#0B1426",
        pointBorderWidth: 2,
        borderWidth: 2.5,
      },
      {
        label: "Pengeluaran",
        data: [2400, 1398, 9800, 3908, 4800, 3800],
        borderColor: "#f87171",
        backgroundColor: "rgba(248, 113, 113, 0.08)",
        fill: true,
        tension: 0.4,
        pointRadius: 4,
        pointHoverRadius: 7,
        pointBackgroundColor: "#f87171",
        pointBorderColor: "#0B1426",
        pointBorderWidth: 2,
        borderWidth: 2.5,
      },
    ],
  };

  const saldoData = {
    labels: chartLabels,
    datasets: [
      {
        label: "Saldo",
        data: [1600, 1602, -7800, -1128, -2910, -1410],
        borderColor: "#a78bfa",
        backgroundColor: "rgba(167, 139, 250, 0.08)",
        fill: true,
        tension: 0.4,
        pointRadius: 5,
        pointHoverRadius: 8,
        pointBackgroundColor: "#a78bfa",
        pointBorderColor: "#0B1426",
        pointBorderWidth: 2,
        borderWidth: 2.5,
      },
    ],
  };

  /* ---------- Chart Options ---------- */
  const lineOptions = {
    responsive: true,
    maintainAspectRatio: false,
    interaction: { mode: "index", intersect: false },
    plugins: {
      legend: {
        position: "top",
        align: "end",
        labels: {
          usePointStyle: true,
          pointStyle: "circle",
          padding: 20,
          font: { size: 12, weight: "500" },
        },
      },
      tooltip: {
        ...TOOLTIP_STYLE,
        callbacks: {
          label: (ctx) => {
            const val = ctx.parsed.y;
            const label = ctx.dataset.label;
            if (["Pemasukan", "Pengeluaran", "Saldo"].includes(label)) {
              return `${label}: ${formatCurrency(val)}`;
            }
            return `${label}: ${val}`;
          },
        },
      },
    },
    scales: {
      x: { grid: { display: false }, ticks: TICK_STYLE },
      y: {
        grid: GRID_STYLE,
        ticks: {
          ...TICK_STYLE,
          callback: (value) => {
            if (Math.abs(value) >= 1000) return formatCurrency(value);
            return value;
          },
        },
      },
    },
  };

  return (
    <>
      <Helmet>
        <title>Dashboard Overview - Radius Trend</title>
        <meta
          name="description"
          content="Dashboard overview Radius Trend — monitoring pemasukan, pengeluaran, saldo, pertumbuhan user PPPoE dan reseller secara real-time."
        />
      </Helmet>

      <DashboardLayout showSidebar={showSidebar} setShowSidebar={setShowSidebar}>
        {/* ===== Header ===== */}
        <Header toggleSidebar={() => setShowSidebar(!showSidebar)}>
          <div className="flex flex-col">
            <h2 className="text-lg md:text-xl font-display font-bold text-gray-100 tracking-tight">
              Dashboard Overview 
            </h2>
            <p className="text-xs text-slate-500 hidden md:block mt-0.5">
              Selamat datang kembali,{" "}
              <span className="text-slate-300 font-medium">
                {userData?.name?.split(" ")[0]}
              </span>
              !
            </p>
          </div>

          {/* Profile */}
          <div className="flex items-center gap-3 pl-4 border-l border-slate-700/50">
            <div className="hidden sm:flex flex-col text-right">
              <span className="text-sm font-semibold text-gray-200 leading-none mb-1">
                {userData?.name}
              </span>
              <span className="text-[10px] font-semibold text-ocean-400 uppercase tracking-wider leading-none">
                {userData?.role?.name}
              </span>
            </div>
            <div className="relative group cursor-pointer">
              {userData?.image ? (
                <img
                  src={`${BaseUrl}/storage/${userData.image}`}
                  alt="Profile"
                  className="w-10 h-10 rounded-xl object-cover ring-2 ring-slate-700/50 group-hover:ring-ocean-600/50 transition-all"
                />
              ) : (
                <div
                  className="w-10 h-10 rounded-xl flex items-center justify-center text-slate-400 ring-2 ring-slate-700/50 group-hover:ring-ocean-600/50 transition-all"
                  style={{
                    background: "linear-gradient(135deg, rgba(0,180,216,0.15), rgba(0,119,182,0.08))",
                    border: "1px solid rgba(0,180,216,0.15)",
                  }}
                >
                  <FaCircleUser className="text-lg" />
                </div>
              )}
              <span
                className="absolute bottom-0 right-0 w-3 h-3 rounded-full border-2"
                style={{
                  background: "#34d399",
                  borderColor: "#0B1426",
                }}
              />
            </div>
          </div>
        </Header>

        {/* ===== Content ===== */}
        <main className="p-4 lg:p-6 space-y-6">
          
          {/* Stat Cards - Menggunakan 1 Grid yang Rapi */}
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
            {statCards.map((card) => (
              <StatCard
                key={card.title}
                title={card.title}
                value={card.value}
                icon={card.icon}
                gradient={card.gradient}
                iconBg={card.iconBg}
                trend={card.trend}
                isLoading={loading} // Menggunakan loading status dari hook
              />
            ))}
          </div>

          {/* Line Charts */}
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            <ChartCard title="Pemasukan & Pengeluaran">
              <div className="h-[280px] sm:h-[320px]">
                <Line data={pemasukanPengeluaranData} options={lineOptions} />
              </div>
            </ChartCard>

            <ChartCard title="Trend Saldo">
              <div className="h-[280px] sm:h-[320px]">
                <Line data={saldoData} options={lineOptions} />
              </div>
            </ChartCard>
          </div>
          
        </main>
      </DashboardLayout>
    </>
  );
};